import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  TemplateRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  SimpleChanges,
} from "@angular/core";
import { Router } from "@angular/router";
import { UtilityService } from "@common/services/utility.service";
import {
  SBRouteData,
  SideNavItem,
  SideNavItems,
} from "@modules/navigation/models";
import {
  NavigationService,
  SideNavService,
} from "@modules/navigation/services";
import objectHash from "object-hash";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators"; // to reset loaders on completion or cancel
import {
  ModalDismissReasons,
  NgbModal,
  NgbModalOptions,
} from "@ng-bootstrap/ng-bootstrap";
import { SubNavRightComponent } from "@modules/navigation/components";
import { ApiRequestService } from "@common/services/api-request.service";

import { ToastService } from "@common/services/toast.service";
import { SecureStorageService } from "../../../../app/services/secure-storage.service";
import { PermissionsService } from "../../../../app/services/permissions.service";
import { sideNavItemsList, sideNavSections } from "@modules/navigation/data/side-nav-dashboard.data";

@Component({
    selector: "sbpro-side-nav-item",
    templateUrl: "./side-nav-item-pro.component.html",
    styleUrls: ["side-nav-item-pro.component.scss"],
    standalone: false
})
export class SideNavItemProComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild("collapsibleSection")
  collapsibleSection!: ElementRef<HTMLDivElement>;
  @ViewChild("noDevice") noDevice!: TemplateRef<unknown>;
  @ViewChild("noMeteringSections") noMeteringSections!: TemplateRef<unknown>;
  @ViewChild("noMeteringPoints") noMeteringPoints!: TemplateRef<unknown>;

  @Input() sideNavItem!: SideNavItem;
  @Input() hierarchy: string[] = [];
  @Input() reloadNav!: string;
  @Input() searchTerm: string = '';

  isActive = false;
  collapsed!: boolean;
  routeData!: SBRouteData;
  navElement!: HTMLDivElement;
  id!: string;
  hierarchyExtension!: string[];
  subscription: Subscription = new Subscription();
  afterViewInit = false;
  activeLevel = "";
  deviceVersion = "";
  count = 1;
  meteringPoint = "";
  meteringSection = "";
  meteringFarm = "";

  deviceManagement = "";
  meteringSectionManagement = "";
  meteringPointManagement = "";
  companyCheck = localStorage.getItem("company");
  meteringFarmManagement: any;
  // Persisted search term for downstream API calls
  menuSearchTerm: string = '';

  loading = false; // indicates API loading state for submenu

  // Pagination for loading more farms
  private navigationPage: number = 1;
  private navigationPerPage: number = 5;
  loadingMoreFarms: boolean = false;
  loadingMoreSections: boolean = false
  navigationSectionPage: number = 1;
  loadingMorePoints: boolean = false;
  navigationPointsPage: number = 1;
  loadingMoreMeters: boolean = false;
  navigationMetersPage: number = 1;

  constructor(
    public subNavRight: SubNavRightComponent,
    public utilityService: UtilityService,
    private sideNavService: SideNavService,
    public navigationService: NavigationService,
    private router: Router,
    private modalService: NgbModal,
    private apiRequest: ApiRequestService,
    private changeDetectorRef: ChangeDetectorRef,
    private toastService: ToastService,
    private secureStorage:SecureStorageService,
    private permissionsService: PermissionsService,
  ) {}

  private getMeterMenuIcon(meterType: any): string {
    const normalized = (meterType ?? "").toString().trim().toLowerCase();
    if (!normalized) {
      return "assets/dashboard/menu/meter.png";
    }
    let ext = ".svg";
    if (normalized !== "water" && normalized !== "electricity" && normalized !== "gas") {
      ext = ".png";
    }
    return `assets/dashboard/menu/${normalized}${ext}`;
  }

  async ngOnInit() {
    this.menuSearchTerm = (this.searchTerm || '').trim();
    this.id = objectHash(this.sideNavItem);
    this.hierarchyExtension = [...this.hierarchy, this.id];
    if (this.sideNavItem.text === "Dashboards") {
      this.sideNavService.setDefault(this.hierarchyExtension);
    }

    
    // :white_check_mark: Load permissions synchronously from memory (preloaded)
    let permissions:any = this.permissionsService.getAll();
    // let permissions:any = await this.secureStorage.getItem('permissions');
    // permissions =  JSON.parse(permissions);
    this.deviceManagement = permissions.deviceManagement;
    this.meteringSectionManagement = permissions.meteringSectionManagement;
    this.meteringPointManagement = permissions.meteringPointManagement;
    this.meteringFarmManagement = permissions.meteringFarmManagement

    this.collapsed = !this.sideNavService.isExpanded(this.id);
    this.subscription.add(
      this.sideNavService.expand$.subscribe((ids) => {
        const thisIDisInExpandedHierarchy = !!ids.find((id) => id === this.id);
        const sameLevel = ids.length === this.hierarchyExtension.length;

        if (!thisIDisInExpandedHierarchy && sameLevel) {
          if (!this.collapsed) {
            this.collapse();
          }
        }
      })
    );

    if (this.count == 1 && this.sideNavItem.level === "meteringFarm") {
 
      this.collapsed = true;
      this.count++;
    }

    this.subscription.add(
      this.navigationService
        .currentURL$()
        .subscribe((currentURL) =>
          this.determineIfActive(
            currentURL.replace(/#.*$/, ""),
            this.sideNavItem
          )
        )
    );

    this.restoreExpandedSelection();

    if (this.sideNavItem.link) {
      this.sideNavService.saveCache(
        this.hierarchyExtension,
        this.sideNavItem.link
      );
      return this.router.navigate([this.sideNavItem.link]);
    }
    this.changeDetectorRef.detectChanges();
  }

 

  ngAfterViewInit() {
    this.afterViewInit = true;
    this.navElement = this.collapsibleSection.nativeElement;
    this.navElement.addEventListener("transitionend", (event) => {
      if (!this.collapsed) {
        this.navElement.style.height = "";
      }
    });
    if (this.isActive) {
      this.expand();
    }
  }

  // Triggered by a special virtual item with level 'loadMoreFarms'
  loadMoreFarms(): void {
    if (this.loadingMoreFarms) {
      return;
    }
    this.loadingMoreFarms = true;

    // Read last page from storage to keep consistent across instances
    const savedPage = parseInt(localStorage.getItem("navFarmsPage") || "1");
    this.navigationPage = isNaN(savedPage) ? 1 : savedPage;
    this.navigationPage = this.navigationPage + 1;

    const body: any = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
        perPage: this.navigationPerPage,
        page: this.navigationPage,
      },
    };
    if(this.menuSearchTerm){
            body.payload.searchTerm = this.menuSearchTerm
    }

    this.apiRequest
      .apiRequest("metering/navigation/children/", "children.json", body)
      .subscribe(
        (data: any) => {
          if (data && data.success === true) {
            // Remove any existing virtual load more tile to re-append at end
            if (sideNavSections[0] && Array.isArray(sideNavSections[0].items)) {
              sideNavSections[0].items = sideNavSections[0].items.filter(
                (it: any) => it.key !== -1
              );
            }

            if (data.data && Array.isArray(data.data.data)) {
              data.data.data.forEach((element: any) => {
                const kv: any = { key: element.id, value: element.name };
                sideNavSections[0].items.push(kv);

                const meteringSections: any[] = [];
                const dataitem: any = {
                  [element.id]: {
                    icon: "",
                    imgSrc:
                      (localStorage.getItem("company") || "") == "landis"
                        ? "assets/landis-assets/images/meteringfarm.svg"
                        : "assets/dashboard/menu/tree.svg",
                    text: element.name,
                    submenu: meteringSections,
                    key: element.id,
                    level: "meteringFarm",
                  },
                };
                sideNavItemsList.AddData(dataitem);
              });
            }

            // Re-append the virtual load more item at the end only if more results came back
            if (data.data.pagination.next != null) {
              sideNavSections[0].items.push({ key: -1, value: "Load more farms", totalMeters: 0 });
              sideNavItemsList.AddData({
                [-1]: {
                  icon: "",
                  imgSrc: "",
                  text: "Load more farms",
                  submenu: [],
                  key: -1,
                  level: "loadMoreFarms",
                },
              });
            }

            localStorage.setItem("navFarmsPage", String(this.navigationPage));
          }
          this.loadingMoreFarms = false;
          this.changeDetectorRef.detectChanges();
        },
        (_err: any) => {
          this.loadingMoreFarms = false;
          this.changeDetectorRef.detectChanges();
        }
      );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.searchTerm) {
      this.menuSearchTerm = (this.searchTerm || '').trim();
    }
    if (changes.reloadNav?.firstChange) {
      return;
    }
    this.ngOnInit();

    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private restoreExpandedSelection(): void {
    const activeLevel = localStorage.getItem("activeLevel");
    if (!activeLevel) {
      return;
    }

    const selectedId =
      activeLevel === "meteringFarm"
        ? localStorage.getItem("meteringFarmID")
        : activeLevel === "meteringSection"
        ? localStorage.getItem("meteringSectionID")
        : activeLevel === "meteringPoint"
        ? localStorage.getItem("meteringPointID")
        : null;

    if (!selectedId) {
      return;
    }

    if (String(this.sideNavItem.key) !== String(selectedId)) {
      return;
    }

    if (this.sideNavItem.level !== activeLevel) {
      return;
    }

    this.isActive = true;
    this.collapsed = false;
    this.hierarchyExtension.forEach((id) => this.sideNavService.setExpanded(id, true));
    this.sideNavService.saveCache(this.hierarchyExtension, "/dashboard");
    this.sideNavService.expand$.next(this.hierarchyExtension);
    this.changeDetectorRef.markForCheck();
  }

  setActiveLevel(
    activeLevel: string,
    levelID: string,
    meterType: string,
    deviceType: string,
    deviceVersion: string,
    levelName: string,
    activeStatus:string
  ) {
    localStorage.setItem("levelID", levelID);
    localStorage.setItem("activeLevel", activeLevel);
    localStorage.setItem("deviceVersion", deviceVersion);
    localStorage.setItem("meteringPointStatement", "no");

    if(activeStatus){
      localStorage.setItem('deviceActivationStatus' , activeStatus)
    }

    this.activeLevel = activeLevel;
    this.deviceVersion = deviceVersion;

    

    if (activeLevel == "meteringFarm") {
      this.getAccountHierachy(levelID)
      //localStorage.removeItem("meteringSection");
      

      if (localStorage.getItem("meteringFarmID") != levelID) {
        // Clear any stored pagination for sections across farms when switching
        const keysToClear: string[] = [];
        console.log(localStorage , ' localstorage p p p')
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i) as string;
          console.log(k.indexOf("navChildrenSectionPage:") , ' k k k')
          if (k && k.indexOf("navChildrenSectionPage:") === 0) {
            keysToClear.push(k);
          }
        }
        console.log(keysToClear , ' keysToclear')
        keysToClear.forEach((k) => localStorage.removeItem(k));
        this.navigationSectionPage = 1;

        localStorage.setItem("showReporting", "yes");
        localStorage.setItem("meteringFarm", levelName);
        localStorage.setItem("meteringFarmID", levelID);
      }
      this.meteringFarm = levelName;

      // Start fresh for this farm; sections will be fetched on expand
      //this.getMeteringSectionsByMeteringFarm()
      this.navigateToDetailView("meteringFarm");
      this.changeDetectorRef.detectChanges();
    } else if (activeLevel == "meteringSection") {
      localStorage.removeItem("meteringPointID");
      localStorage.setItem("showReporting", "yes");
      localStorage.setItem("meteringSectionID", levelID);
      this.meteringSection = levelName;
      //this.getMeteringPoints();
      this.changeDetectorRef.detectChanges();
      this.navigateToDetailView("meteringSection");
      //localStorage.setItem("dateFilter", "");
      localStorage.setItem("meteringSection", levelName);
      localStorage.setItem('farmMeteringSection', levelName)
    } else if (activeLevel == "meteringPoint") {
      localStorage.removeItem("deviceID");

      localStorage.setItem("showReporting", "yes");
      localStorage.setItem("meteringPointID", levelID);

      localStorage.setItem("meteringPointStatement", "yes");

      this.meteringPoint = levelName;

      //this.getMeteringPointMeters(levelName);
      this.navigateToDetailView("meteringPoint");
      this.changeDetectorRef.detectChanges();
    } else {




    
     

     


      this.getSupoportedDevices(deviceType)





      localStorage.setItem("deviceType", deviceType);
      localStorage.setItem("meterType", meterType);
      if (meterType == "temp") {
        localStorage.setItem("unit", "°C");
      }
      localStorage.setItem("deviceID", levelID);
      localStorage.setItem("deviceVersion", deviceVersion);
      localStorage.setItem("device", levelName);

      const targetUrl = "/dashboard/" + activeLevel;
      const currentUrl = (this.router.url || "").split("?")[0];
      const isSameDashboardDeviceRoute = activeLevel === "device" && currentUrl === "/dashboard/device";
      const eventDetail = {
        deviceID: levelID,
        deviceType,
        meterType,
        deviceVersion,
        levelName,
        activeStatus,
        source: "side-nav-item-pro",
      };

      // Same-route device switch: refresh dashboard component without full page reload.
      if (isSameDashboardDeviceRoute) {
        localStorage.setItem("active", "dashboard");
        this.router
          .navigateByUrl("/dashboard/metering", { skipLocationChange: true })
          .then(() => this.router.navigateByUrl("/dashboard/device"));
      } else if (localStorage.getItem("active") != "dashboard") {
        this.router.navigateByUrl(targetUrl);
        localStorage.setItem("active", "dashboard");
      } else {
        this.router.navigateByUrl("/dashboard/metering");
        setTimeout(() => {
          this.router.navigateByUrl(targetUrl);
          if (activeLevel === "device") {
            window.dispatchEvent(new CustomEvent("meterDashboardDeviceChanged", { detail: eventDetail }));
          }
        }, 500);
      }
    }
    this.subNavRight.ngOnInit();
    this.changeDetectorRef.detectChanges();
  }

  private navigateToDetailView(activeLevel: "meteringFarm" | "meteringSection" | "meteringPoint") {
    const target =
      activeLevel === "meteringFarm"
        ? "/dashboard/metering-farm-details"
        : activeLevel === "meteringSection"
        ? "/dashboard/metering-section-details"
        : "/dashboard/metering-point-details";

    if (this.router.url === target) {
      this.router
        .navigateByUrl("/dashboard", { skipLocationChange: true })
        .then(() => this.router.navigateByUrl(target));
      return;
    }

    this.router.navigateByUrl(target);
  }



  getSupoportedDevices(deviceType:any) {
    let body = {
        payload: {
            platform: "utilities",
            installationTypeID: "all",
        },
    };

    this.apiRequest.apiRequest("utilities/supported_device_types/", "supported_device_types.json", body)
        .subscribe((res) => {
            if (res.success) {
              const devices: any[] = Array.isArray(res?.data?.deviceTypes) ? res.data.deviceTypes : [];
              const filteredDevice = devices.find((item:any) => item?.deviceType == deviceType);
              const requiresModem = filteredDevice?.requiresModem ?? 'no';
              localStorage.setItem("requiresModem", requiresModem);


            }
            this.changeDetectorRef.detectChanges();
    });
}

  getAccounts(
    activeLevel: string,
    levelID: string,
    pmLevel: string,
    levelName: string,
    textLevel: string
  ) {

    localStorage.setItem("accountsLevelID", levelID);
    localStorage.setItem("activeLevel", activeLevel);
    localStorage.setItem("accountspmLevel", pmLevel);
    localStorage.setItem("activeLink", levelName + " -- " + textLevel);

    this.activeLevel = activeLevel;
    if (activeLevel == "meteringFarm") {
      localStorage.removeItem("meteringSection");
      if (localStorage.getItem("meteringFarmID") != levelID) {
        localStorage.setItem("meteringFarmID", levelID);
      }

      if(levelName == 'All Accounts' || levelName == 'Admins Only' ){
      this.router.navigateByUrl("/dashboard/metering");
      setTimeout(() => {
        this.router.navigateByUrl("/dashboard/users");
      }, 0);
      }
       this.changeDetectorRef.detectChanges();
    } else if (activeLevel == "meteringSection") {
      localStorage.removeItem("meteringPointID");
      localStorage.setItem("showReporting", "yes");
      localStorage.setItem("meteringSectionID", levelID);

      if(levelName == 'All Accounts' || levelName == 'Managers Only' ){
      this.router.navigateByUrl("/dashboard/metering");
      setTimeout(() => {
        this.router.navigateByUrl("/dashboard/users");
      }, 0);
       }
      
      this.changeDetectorRef.detectChanges();
    } else if (activeLevel == "meteringPoint") {
      localStorage.removeItem("deviceID");
      localStorage.setItem("showReporting", "yes");
      localStorage.setItem("meteringPointID", levelID);

      if(levelName == 'All Accounts' || levelName == 'Editors Only' || levelName == 'Viewers Only' ){
      this.router.navigateByUrl("/dashboard/metering");
      setTimeout(() => {
        this.router.navigateByUrl("/dashboard/users");
      }, 0);
      }
     

      this.changeDetectorRef.detectChanges();
    }
    this.changeDetectorRef.detectChanges();
  }

  getMeteringSections() {
    this.loading = true;
    let body = {
      payload: {
        meteringFarmID: parseInt(
          localStorage.getItem("meteringFarmID") as string
        ),
        platform: "web",
      },
    };
    const defaultOptions = {
      autohide: true,
      delay: 5000,
    };
    this.apiRequest
      .apiRequest(
        "metering/metering_sections_list/",
        "metering_sections_list.json",
        body
      )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe((res) => {
        if (res.data.meteringSections && res.data.meteringSections.length < 1) {
          this.modalService.open(this.noMeteringSections).result.then(
            (result) => {
              if (result === "addMeteringSection") {
                localStorage.setItem(
                  "meteringSectionAction",
                  "add_metering_section"
                );
                this.router.navigateByUrl(
                  "/dashboard/metering/meteringSection"
                );
                //window.location.assign("/dashboard/metering/meteringSection")
              }
                 if (result === "editMeteringFarm") {
                if (
                  this.meteringFarmManagement == "viewer" ||
                  this.meteringFarmManagement == "no-access"
                ) {
                  let activeLevel = ("Metering Farm " +
                    localStorage.getItem("meteringFarm")) as string;
                  this.toastService.show(
                    "Edit Warning",
                    "You do not have permissions to edit \n" + activeLevel,
                    {
                      headerClasses: "bg-warning text-light",
                      ...defaultOptions,
                    }
                  );
                } else {
                  this.router.navigateByUrl("/dashboard/metering");
                  localStorage.setItem(
                    "meteringFarmAction",
                    "update_metering_farm"
                  );
                  setTimeout(() => {
                    this.router.navigateByUrl(
                      "/dashboard/metering/meteringFarmForm"
                    );
                  }, 500);
                  //window.location.assign("/dashboard/metering/meteringSection")
                }
              }
            },
            (reason) => {}
          );
        }

        this.changeDetectorRef.detectChanges();
        this.loading = false;
      });
  }

  getMeteringSectionName(meteringFarms: any[], targetFarmID: number): string | null {
    const targetIndex = meteringFarms.findIndex(farm => farm.meteringFarmID === targetFarmID);
  
    if (targetIndex === -1) return null; // Target farm not found
  
    for (let i = targetIndex; i < meteringFarms.length; i++) {
      const farm = meteringFarms[i];
      if (farm.meteringSections && farm.meteringSections.length > 0) {
        const firstSection = farm.meteringSections[0];
        return firstSection?.meteringSectionName || null;
      }
    }
  
    return null;
  }

  getMeteringPoints() {
    this.loading = true;
    let body = {
      payload: {
        meteringSectionID: parseInt(
          localStorage.getItem("meteringSectionID") as string
        ),
        platform: "web",
      },
    };
    const defaultOptions = {
      autohide: true,
      delay: 5000,
    };

    this.apiRequest
      .apiRequest(
        "metering/metering_points_list/",
        "metering_points_list.json",
        body
      )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe((res) => {
        if (res.data.meteringPoints && res.data.meteringPoints.length < 1) {
          this.modalService.open(this.noMeteringPoints).result.then(
            (result) => {
              if (result === "add") {
                this.router.navigateByUrl("/dashboard/metering");
                localStorage.setItem(
                  "meteringPointAction",
                  "add_metering_point"
                );
                setTimeout(() => {
                  this.router.navigateByUrl(
                    "/dashboard/metering/meteringPoint"
                  );
                }, 500);
                //window.location.assign("/dashboard/metering/meteringPoint")
              }
              if (result === "editMeteringSection") {
                if (
                  this.meteringSectionManagement == "viewer" ||
                  this.meteringSectionManagement == "no-access"
                ) {
                  let activeLevel = ("Metering Section " +
                    localStorage.getItem("meteringSection")) as string;
                  this.toastService.show(
                    "Edit Warning",
                    "You do not have permissions to edit \n" + activeLevel,
                    {
                      headerClasses: "bg-warning text-light",
                      ...defaultOptions,
                    }
                  );
                } else {
                  this.router.navigateByUrl("/dashboard/metering");
                  localStorage.setItem(
                    "meteringSectionAction",
                    "update_metering_section"
                  );
                  setTimeout(() => {
                    this.router.navigateByUrl(
                      "/dashboard/metering/meteringSection"
                    );
                  }, 500);
                  //window.location.assign("/dashboard/metering/meteringSection")
                }
              }
            },
            (reason) => {}
          );
        }

        this.changeDetectorRef.detectChanges();
        this.loading = false;
      });
  }

  viewMeteringPointStatement(
    activeLevel: string,
    levelID: string,
    levelName: string
  ) {
    localStorage.setItem("activeLevel", activeLevel);
    localStorage.setItem("meteringPointStatement", "no");
    localStorage.setItem("statement", levelID);
    localStorage.setItem("credit", levelID);
    localStorage.setItem("meteringPointID", levelID);

    localStorage.removeItem("deviceID");
    localStorage.removeItem("device");

    localStorage.setItem("showReporting", "yes");
    localStorage.setItem("meteringPointStatement", "yes");

    this.meteringPoint = levelName;
    this.activeLevel = activeLevel;

    this.changeDetectorRef.detectChanges();

    //if(localStorage.getItem("active") == "dashboard"){
    if (localStorage.getItem("meteringPoint") != levelName) {
      this.router.navigateByUrl("/dashboard");
    }
    localStorage.setItem("meteringPoint", levelName);

    setTimeout(() => {
      this.router.navigateByUrl("/dashboard/meteringPoint");
    }, 500);
    //}
    this.subNavRight.ngOnInit();

    this.changeDetectorRef.detectChanges();
  }

  viewCreditProfile(activeLevel: string, levelID: string, levelName: string) {
    localStorage.setItem("activeLevel", activeLevel);
    localStorage.setItem("meteringPointStatement", "no");
    localStorage.setItem("statement", levelID);
    localStorage.setItem("credit", levelID);
    localStorage.setItem("meteringPointID", levelID);

    localStorage.removeItem("deviceID");
    localStorage.removeItem("device");

    localStorage.setItem("showReporting", "yes");
    localStorage.setItem("meteringPointStatement", "yes");

    this.meteringPoint = levelName;
    this.activeLevel = activeLevel;

    this.changeDetectorRef.detectChanges();

    //if(localStorage.getItem("active") == "dashboard"){
    if (localStorage.getItem("meteringPoint") != levelName) {
      this.router.navigateByUrl("/dashboard");
    }
    localStorage.setItem("meteringPoint", levelName);

    setTimeout(() => {
      this.router.navigateByUrl("/dashboard/creditProfile");
    }, 500);
    //}
    this.subNavRight.ngOnInit();

    this.changeDetectorRef.detectChanges();
  }

  viewModems(activeLevel: string, levelID: string, levelName: string) {





    localStorage.setItem("activeLevel", activeLevel);
    localStorage.setItem("meteringPointStatement", "no");
    localStorage.setItem("modems", levelID);
    localStorage.removeItem("meteringPointID");
    localStorage.setItem("showReporting", "yes");
    localStorage.setItem("meteringSectionID", levelID);
    localStorage.removeItem("deviceID");
    localStorage.removeItem("device");
    localStorage.setItem("meteringPointStatement", "no");

    this.activeLevel = activeLevel;

    this.changeDetectorRef.detectChanges();

    this.router.navigateByUrl("/dashboard");

    setTimeout(() => {
      this.router.navigateByUrl("/dashboard/metering/modems");
    }, 500);
    this.subNavRight.ngOnInit();

    this.changeDetectorRef.detectChanges();
  }

  getMeteringPointMeters(levelName: any) {
    this.loading = true;
    const defaultOptions = {
      autohide: true,
      delay: 5000,
    };

    let body = {
      payload: {
        meteringPointID: parseInt(
          localStorage.getItem("meteringPointID") as string
        ),
        platform: "web",
      },
    };
    this.apiRequest
      .apiRequest(
        "metering/metering_point_meters_list/",
        "metering_point_meters_list.json",
        body
      )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe((res) => {
        if (res.data.meters && res.data.meters.length < 1) {
          this.modalService.open(this.noDevice).result.then(
            (result) => {
              if (result === "add") {
                this.router.navigateByUrl("/dashboard/metering");

                localStorage.setItem("deviceAction", "add");
                setTimeout(() => {
                  this.router.navigateByUrl("/dashboard/metering/deviceSelect");
                }, 500);
              }
              if (result === "editMeteringPoint") {
                if (
                  this.meteringPointManagement == "viewer" ||
                  this.meteringPointManagement == "no-access"
                ) {
                  let activeLevel = ("Metering Point " +
                    localStorage.getItem("meteringPoint")) as string;
                  this.toastService.show(
                    "Edit Warning",
                    "You do not have permissions to edit \n" + activeLevel,
                    {
                      headerClasses: "bg-warning text-light",
                      ...defaultOptions,
                    }
                  );
                } else {
                  this.router.navigateByUrl("/dashboard/metering");
                  localStorage.setItem(
                    "meteringPointAction",
                    "update_metering_point"
                  );
                  setTimeout(() => {
                    this.router.navigateByUrl(
                      "/dashboard/metering/meteringPoint"
                    );
                  }, 500);
                  //window.location.assign("/dashboard/metering/meteringPoint")
                }
              }
            },
            (reason) => {}
          );
        }

        this.changeDetectorRef.detectChanges();
        this.loading = false;
      });
  }

  determineIfActive(url: string, sideNavItem: SideNavItem) {
    // this.isActive = false;

    if (sideNavItem.link) {
      this.isActive = url === sideNavItem.link;
      this.changeDetectorRef.markForCheck();
    }

    if (sideNavItem.submenu) {
      sideNavItem.submenu.forEach((submenu) =>
        this.determineIfActiveParent(url, submenu)
      );
    } else {
      if (localStorage.getItem("device")) {
        this.isActive = localStorage.getItem("device") === sideNavItem.text;
        this.changeDetectorRef.markForCheck();
      } else if (localStorage.getItem("statement")) {
        this.isActive =
          localStorage.getItem("statement") === sideNavItem.key?.toString();
        this.changeDetectorRef.markForCheck();
      } else if (localStorage.getItem("credit")) {
        this.isActive =
          localStorage.getItem("credit") === sideNavItem.key?.toString();
        this.changeDetectorRef.markForCheck();
      } else if (localStorage.getItem("modems")) {
        this.isActive =
          localStorage.getItem("modems") === sideNavItem.key?.toString();
        this.changeDetectorRef.markForCheck();
      }
    }

    this.changeDetectorRef.markForCheck();
  }

  determineIfActiveParent(url: string, sideNavItem: SideNavItem) {
    if (sideNavItem.link && url === sideNavItem.link) {
      this.isActive = true;
      if (this.afterViewInit) {
        this.expand();
      }
      this.changeDetectorRef.markForCheck();
    }
    if (sideNavItem.submenu) {
      sideNavItem.submenu.forEach((submenu) =>
        this.determineIfActiveParent(url, submenu)
      );
    }
  }

  toggle(activeLevel: string, levelName: string, userAccount: string , data:any) {

    const defaultOptions = {
      autohide: true,
      delay: 10000,
    };

    localStorage.setItem("activeLevel", activeLevel);

    if (activeLevel == "meteringFarm" && userAccount != "yes") {
      localStorage.setItem("meteringFarm", levelName);
    } else if (activeLevel == "meteringSection" && userAccount != "yes") {
      localStorage.setItem("meteringSection", levelName);
    } else if (activeLevel == "meteringPoint" && userAccount != "yes") {
      setTimeout(() => {
        localStorage.setItem("meteringPoint", levelName);
      }, 2000);
    }

    if (this.sideNavItem.link) {
      this.sideNavService.saveCache(
        this.hierarchyExtension,
        this.sideNavItem.link
      );
      return this.router.navigate([this.sideNavItem.link]);
    }
    if (this.collapsed) {
      // For metering farms, sections, and points, always refetch children on expand to avoid stale data
      const shouldFetchChildren =
        (this.sideNavItem.level == "meteringFarm" || this.sideNavItem.level == "meteringSection" || this.sideNavItem.level == "meteringPoint")
          ? true
          : (this.sideNavItem.submenu != null && this.sideNavItem.submenu.length < 1);
      if (shouldFetchChildren) {


        if (this.sideNavItem.level == "meteringFarm") {
          let body: any = {
            payload: {
              userID: parseInt(localStorage.getItem("userID") as string),
              parentType:'farm', //farm,section,point
              parentID: localStorage.getItem("meteringFarmID") as string,
              perPage:5,
              page:1
            },
          };

         
          if(this.menuSearchTerm){
            body.payload.searchTerm = this.menuSearchTerm
          }

          this.loading = true;
          this.apiRequest
            .apiRequest(
              "metering/navigation/children/",
              "children.json",
             body
            )
            .pipe(
              finalize(() => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();
              })
            )
            .subscribe((data) => {
              if (data.success == true) {

          if (data.data.data && data.data.data.length < 1) {
          this.modalService.open(this.noMeteringSections).result.then(
            (result) => {
              if (result === "addMeteringSection") {
                localStorage.setItem(
                  "meteringSectionAction",
                  "add_metering_section"
                );
                this.router.navigateByUrl(
                  "/dashboard/metering/meteringSection"
                );
                //window.location.assign("/dashboard/metering/meteringSection")
              }
                 if (result === "editMeteringFarm") {
                if (
                  this.meteringFarmManagement == "viewer" ||
                  this.meteringFarmManagement == "no-access"
                ) {
                  let activeLevel = ("Metering Farm " +
                    localStorage.getItem("meteringFarm")) as string;
                  this.toastService.show(
                    "Edit Warning",
                    "You do not have permissions to edit \n" + activeLevel,
                    {
                      headerClasses: "bg-warning text-light",
                      ...defaultOptions,
                    }
                  );
                } else {
                  this.router.navigateByUrl("/dashboard/metering");
                  localStorage.setItem(
                    "meteringFarmAction",
                    "update_metering_farm"
                  );
                  setTimeout(() => {
                    this.router.navigateByUrl(
                      "/dashboard/metering/meteringFarmForm"
                    );
                  }, 500);
                  //window.location.assign("/dashboard/metering/meteringSection")
                }
              }
            },
            (reason) => {}
          );
        }else{
 this.sideNavItem.submenu = [];
                if (
                  data.data.data.length > 0 &&
                  this.meteringSectionManagement != "no-access"
                ) {
                  data.data.data.forEach(
                    (element: any, index: any, array: any[]) => {
                      let dataitem: SideNavItem = {
                        icon: "",
                        imgSrc:
                           this.companyCheck == "landis"
                             ? "assets/landis-assets/images/meteringsectionicon.svg"
                             : "assets/dashboard/menu/building.svg",
                        text: element.name,
                        submenu: [],
                        key: element.id,
                        level: "meteringSection",
                      };
                      this.sideNavItem.submenu?.push(dataitem);

                    this.changeDetectorRef.detectChanges();
                      if (index === array.length - 1) {
                        this.changeDetectorRef.markForCheck();
                        return this.expand();
                      }
                      index = index + 1;
                    }
                  );

                  
                  if (data.data.pagination.next != null) {
                    const meteringSectionLoadingBtn: any = {
                      type: "loadMoreSections",
                      text: "Load More Sections",
                      submenu: [],
                      // store the farm ID on key so loader knows which parent to fetch for
                      key: Number(localStorage.getItem("meteringFarmID")),
                      level: "meteringFarm",
                      // keep a direct reference to the parent farm item to append into
                      parentRef: this.sideNavItem,
                    };
                    this.sideNavItem.submenu?.push(meteringSectionLoadingBtn as SideNavItem);
                  }
              


                } else {
                  this.toastService.show(
                    "No Metering Sections Found",
                    "You do not have any Metering Sections \n",
                    {
                      headerClasses: "bg-warning text-light",
                      ...defaultOptions,
                    }
                  );
                }
        }


               
              } else {
                console.log("request failed");
              }
              this.loading = false;
            });
        } else if (this.sideNavItem.level == "meteringSection") {
          this.loading = true;
          // Reset points pagination for this section when re-expanding
          const pointsPageKey = `navChildrenPointsPage:${this.sideNavItem.key}`;
          localStorage.removeItem(pointsPageKey);
          this.navigationPointsPage = 1;
           let body: any = {
            payload: {
              userID: parseInt(localStorage.getItem("userID") as string),
              parentType:'section', //farm,section,point
              parentID: this.sideNavItem.key,
              perPage:5,
              page:1
            },
          };

          if(this.menuSearchTerm){
            body.payload.searchTerm = this.menuSearchTerm
          }

          this.apiRequest
            .apiRequest(
              "metering/navigation/children/",
              "children.json",
              body
            )
            .pipe(
              finalize(() => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();
              })
            )
            .subscribe((data) => {
              if (data.success == true) {
                this.sideNavItem.submenu = [];
                if (
                  data.data.data &&
                  this.meteringPointManagement != "no-access"
                ) {

                    let dataitem1: SideNavItem = {
                        type: "modems",
                        icon: "",
                        userAccount: "yes",
                        imgSrc:
                      this.companyCheck == "landis"
                        ? "assets/landis-assets/images/modems.png"
                        : "assets/dashboard/menu/modem.png",
                        text: "Modems/Gateways",
                        submenu: [],
                        key: data.data.data[0].meteringSectionID,
                        level: "modems",
                      };
                      this.sideNavItem.submenu?.push(dataitem1);
                  
                  data.data.data.forEach(
                    (element: any, index: any, array: any[]) => {
                      let dataitem: SideNavItem = {
                        icon: "",
                       imgSrc: this.companyCheck == "landis"
                                ? "assets/landis-assets/images/verticalbuilding.svg"
                                : "assets/dashboard/menu/vertical-building.svg",
                        text: element.name,
                        totalMeters: element.totalMeters,
                        submenu: [],
                        key: element.id,
                        level: "meteringPoint",
                      };
                      this.sideNavItem.submenu?.push(dataitem);
                      this.changeDetectorRef.detectChanges();
                      if (index === array.length - 1) {
                        this.changeDetectorRef.markForCheck();
                        return this.expand();
                      }
                      index = index + 1;
                    }
                  );

                  // add load more points tile if next page exists
                  if (data.data.pagination && data.data.pagination.next != null) {
                    const loadMorePointsBtn: any = {
                      type: 'loadMorePoints',
                      text: 'Load More Points',
                      submenu: [],
                      key: this.sideNavItem.key, // section id
                      level: 'meteringSection',
                      parentRef: this.sideNavItem,
                    };
                    this.sideNavItem.submenu?.push(loadMorePointsBtn as SideNavItem);
                  }
                } else {
                  this.toastService.show(
                    "No Metering Points Found",
                    "You do not have any Metering Points \n Please contact your admin",
                    {
                      headerClasses: "bg-warning text-light",
                      ...defaultOptions,
                    }
                  );
                }
              }
              this.loading = false;
            });
        } else if (this.sideNavItem.level == "meteringPoint") {
          // Reset meters pagination for this point when re-expanding
          const metersPageKey = `navChildrenMetersPage:${this.sideNavItem.key}`;
          localStorage.removeItem(metersPageKey);
          this.navigationMetersPage = 1;

          let body: any = {
            payload: {
              userID: parseInt(localStorage.getItem("userID") as string),
              parentType:'point', //farm,section,point
              parentID: this.sideNavItem.key,
              isActive: 'y',
              perPage:5,
              page:1
            },
          };

          if(this.menuSearchTerm){
            body.payload.searchTerm = this.menuSearchTerm
          }
      
          this.loading = true;
          this.apiRequest
            .apiRequest(
            "metering/navigation/children/",
            "children.json",
              body
            )
            .pipe(
              finalize(() => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();
              })
            )
            .subscribe((data) => {
                if (data.success == true) {
                  const walletReadyFlag = (data.data && typeof data.data.walletReady !== "undefined")
                    ? !!data.data.walletReady
                    : false;
                  localStorage.setItem("walletReady", String(walletReadyFlag));
                this.sideNavItem.submenu = [];
                if (data.data.data && this.deviceManagement != "no-access") {
                  //only display for where there is meters
                 
                    if(data.data.thinPrepaymentStatus == 'enabled'){
                        this.sideNavItem.submenu?.push({
                          type: "wallet",
                          icon: "",
                          submenu: [],
                          userAccount: "yes",
                          imgSrc: "assets/dashboard/wallet.png",
                          text: "Wallet Dashboard",
                          key: data.data.data[0].meteringPointID,
                          level: "wallet",
                        });
                       }else{
                       this.sideNavItem.submenu?.push({
                          type: "statement",
                          icon: "",
                          submenu: [],
                          userAccount: "yes",
                          imgSrc: "assets/dashboard/menu/doc.png",
                          text: "View Statement",
                          key: data.data.data[0].meteringPointID,
                          level: "statement",
                        });
                       }

                  this.changeDetectorRef.detectChanges();

                  data.data.data.forEach(
                    (element: any, index: any, array: any[]) => {
                      let dataitem: any = {
                        icon: "",
                        imgSrc: this.getMeterMenuIcon(element.meterType),
                        text: element.name,
                        submenu: null,
                        key: element.deviceID,
                        meterType: element.meterType,
                        deviceType: element.deviceType,
                        deviceVersion: element.deviceVersion ?? element.version ?? element.deviceModel ?? "",
                        level: "device",
                        isActive:element.isActive
                      };
                      this.sideNavItem.submenu?.push(dataitem);

                    
                      this.changeDetectorRef.detectChanges();
                      if (index === array.length - 1) {
                        this.changeDetectorRef.markForCheck();
                        return this.expand();
                      }
                      index = index + 1;
                    }
                     
                  );
                  
                  // Add load more meters tile if there are more pages
                  if (data.data.pagination && data.data.pagination.next != null) {
                    const loadMoreMetersBtn: any = {
                      type: 'loadMoreMeters',
                      text: 'Load More Meters',
                      submenu: [],
                      key: this.sideNavItem.key,
                      level: 'meteringPoint',
                      parentRef: this.sideNavItem,
                    };
                    this.sideNavItem.submenu?.push(loadMoreMetersBtn);
                  }
                 
                } else {
                  this.toastService.show(
                    "No Devices Found",
                    "You do not have any Devices \n Please contact your admin",
                    {
                      headerClasses: "bg-warning text-light",
                      ...defaultOptions,
                    }
                  );
                }
              }
              this.loading = false;
            });
        }
      } else {
        return this.expand();
      }
    } else {
      return this.collapse();
    }
  }

  toggleAccount(activeLevel: string, levelName: string, userAccount: string , levelID:any) {

    const defaultOptions = {
      autohide: true,
      delay: 10000,
    };

    localStorage.setItem("activeLevel", activeLevel);

    if (activeLevel == "meteringFarm" && userAccount != "yes") {
      localStorage.setItem("meteringFarm", levelName);
    } else if (activeLevel == "meteringSection" && userAccount != "yes") {
      localStorage.setItem("meteringSection", levelName);
    } else if (activeLevel == "meteringPoint" && userAccount != "yes") {
      localStorage.setItem("meteringPoint", levelName);
    }

    if (this.sideNavItem.link) {
      this.sideNavService.saveCache(
        this.hierarchyExtension,
        this.sideNavItem.link
      );
      return this.router.navigate([this.sideNavItem.link]);
    }
    if (this.collapsed) {
      if (
        this.sideNavItem.submenu != null &&
        this.sideNavItem.submenu.length < 1
      ) {
        if (this.sideNavItem.level == "meteringFarm" &&  this.sideNavItem.text != 'All Accounts' && this.sideNavItem.text != 'Admins Only' ) {
          this.loading = true;

            let body: any = {
            payload: {
              userID: parseInt(localStorage.getItem("userID") as string),
              parentType:'farm', //farm,section,point
              parentID: this.sideNavItem.key,
              perPage:30,
              page:1
            },
          };

          this.apiRequest
            .apiRequest(
            "metering/navigation/children/",
            "children.json",
            body
            )
            .pipe(
              finalize(() => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();
              })
            )
            .subscribe((data) => {
              if (data.success == true) {
                this.sideNavItem.submenu = [];
                if (
                  data.data.data.length > 0 &&
                  this.meteringSectionManagement != "no-access"
                ) {

                  let meteringSectionAllUsers: SideNavItem = {
                  type: "account",
                  icon: "",
                  userAccount: "yes",
                  pmLevel: "all",
                  imgSrc: this.companyCheck == "landis"
                  ? "assets/landis-assets/images/landisUserAll.png"
                  : "assets/dashboard/menu/userAll.png",
                  text: "All Accounts",
                  textLevel: levelName,
                  submenu: [],
                  key: levelID,
                  level: "meteringFarm",
                };


               this.sideNavItem.submenu?.push(meteringSectionAllUsers);

                let meteringSectionAdminUsers: SideNavItem = {
                  type: "account",
                  icon: "",
                  userAccount: "yes",
                  pmLevel: "1",
                  imgSrc: this.companyCheck == "landis"
                  ? "assets/landis-assets/images/landisUserAdmin.png"
                  : "assets/dashboard/menu/userAdmin.png",
                  text: "Admins Only",
                  textLevel: levelName,
                  submenu: [],
                  key: levelID,
                  level: "meteringFarm",
                };
                this.sideNavItem.submenu?.push(meteringSectionAdminUsers);



                  data.data.data.forEach(
                    (element: any, index: any, array: any[]) => {
                      let dataitem: SideNavItem = {
                        icon: "",
                        imgSrc:
                           this.companyCheck == "landis"
                             ? "assets/landis-assets/images/meteringsectionicon.svg"
                             : "assets/dashboard/menu/building.svg",
                        text: element.name,
                        submenu: [],
                        type:'account',
                        key: element.id,
                        level: "meteringSection",
                      };
                      this.sideNavItem.submenu?.push(dataitem);
                      this.changeDetectorRef.detectChanges();
                      if (index === array.length - 1) {
                        this.changeDetectorRef.markForCheck();
                        return this.expand();
                      }
                      index = index + 1;
                    }
                  );
                } else {
                  this.toastService.show(
                    "No Metering Sections Found",
                    "You do not have any Metering Sections \n",
                    {
                      headerClasses: "bg-warning text-light",
                      ...defaultOptions,
                    }
                  );
                }
              } else {
                console.log("request failed");
              }
              this.loading = false;
            });



        } else if (this.sideNavItem.level == "meteringSection" &&  this.sideNavItem.text != 'All Accounts' && this.sideNavItem.text != 'Managers Only' ) {

          this.loading = true;
          let body: any = {
          payload: {
              userID: parseInt(localStorage.getItem("userID") as string),
              parentType:'section', 
              parentID: this.sideNavItem.key,
              perPage:30,
              page:1
            },
          };

          this.apiRequest
            .apiRequest(
            "metering/navigation/children/",
            "children.json",
            body
            )
            .pipe(
              finalize(() => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();
              })
            )
            .subscribe((data) => {
              if (data.success == true) {
                this.sideNavItem.submenu = [];
                if (
                  data.data.data &&
                  this.meteringPointManagement != "no-access"
                ) {

                 
                      let meteringSectionAllUsers: SideNavItem = {
                        type: "account",
                        icon: "",
                        userAccount: "yes",
                        pmLevel: "all",
                        imgSrc: this.companyCheck == "landis"
                        ? "assets/landis-assets/images/landisUserAll.png"
                        : "assets/dashboard/menu/userAll.png",
                        text: "All Accounts",
                        textLevel: levelName,
                        submenu: [],
                        key: levelID,
                        level: "meteringSection",
                      };
                      this.sideNavItem.submenu?.push(meteringSectionAllUsers);

                      let meteringSectionAdminUsers: SideNavItem = {
                        type: "account",
                        icon: "",
                        userAccount: "yes",
                        pmLevel: "2",
                        imgSrc: this.companyCheck == "landis"
                        ? "assets/landis-assets/images/landisUserAdmin.png"
                        : "assets/dashboard/menu/userAdmin.png",
                        text: "Managers Only",
                        textLevel:levelName,
                        submenu: [],
                        key: levelID,
                        level: "meteringSection",
                      };
                     this.sideNavItem.submenu?.push(meteringSectionAdminUsers);
                  

                
                  data.data.data.forEach(
                    (element: any, index: any, array: any[]) => {
                      let dataitem: SideNavItem = {
                        type:'account',
                        icon: "",
                        imgSrc: this.companyCheck == "landis"
                                ? "assets/landis-assets/images/verticalbuilding.svg"
                                : "assets/dashboard/menu/vertical-building.svg",
                        text: element.name,
                        totalMeters: element.totalMeters,
                        submenu: [],
                        key: element.id,
                        level: "meteringPoint",
                      };
                      this.sideNavItem.submenu?.push(dataitem);
                      this.changeDetectorRef.detectChanges();
                      if (index === array.length - 1) {
                        this.changeDetectorRef.markForCheck();
                        return this.expand();
                      }
                      index = index + 1;
                    }
                  );
                } else {
                  this.toastService.show(
                    "No Metering Points Found",
                    "You do not have any Metering Points \n Please contact your admin",
                    {
                      headerClasses: "bg-warning text-light",
                      ...defaultOptions,
                    }
                  );
                }
              }
              this.loading = false;
            });


        } else if (this.sideNavItem.level == "meteringPoint" &&  this.sideNavItem.text != 'All Accounts' && this.sideNavItem.text != 'Editors Only' && this.sideNavItem.text != 'Viewers Only' ) {
          
          this.loading = true;
          let body: any = {
          payload: {
              userID: parseInt(localStorage.getItem("userID") as string),
              parentType:'point', 
              parentID: this.sideNavItem.key,
              isActive: 'y',
              perPage:30,
              page:1
            },
          };
          this.apiRequest
            .apiRequest(
              "metering/navigation/children/",
            "children.json",
             body
            )
            .pipe(
              finalize(() => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();
              })
            )
            .subscribe((data) => {
              if (data.success == true && !this.sideNavItem.userAccount) {
                this.sideNavItem.submenu = [];

                let allUsers: SideNavItem = {
                  type: "account",
                  icon: "",
                  userAccount: "yes",
                  pmLevel: "all",
                  imgSrc:
                    this.companyCheck == "landis"
                      ? "assets/landis-assets/images/landisUserAll.png"
                      : "assets/dashboard/menu/userAll.png",
                  text: "All Accounts",
                  textLevel: this.sideNavItem.text,
                  submenu: [],
                  key: this.sideNavItem.key,
                  level: "meteringPoint",
                };
                this.sideNavItem.submenu?.push(allUsers);

                let editorUsers: SideNavItem = {
                  type: "account",
                  icon: "",
                  userAccount: "yes",
                  pmLevel: "3",
                  imgSrc:
                    this.companyCheck == "landis"
                      ? "assets/landis-assets/images/landisUserEditor.png"
                      : "assets/dashboard/menu/userEditor.png",
                  text: "Viewers Only",
                  textLevel: this.sideNavItem.text,
                  submenu: [],
                  key: this.sideNavItem.key,
                  level: "meteringPoint",
                };
                this.sideNavItem.submenu?.push(editorUsers);

                // let viewerUsers: SideNavItem = {
                //   type: "account",
                //   icon: "",
                //   userAccount: "yes",
                //   pmLevel: "4",
                //   imgSrc:
                //     this.companyCheck == "landis"
                //       ? "assets/landis-assets/images/landisUserViewer.png"
                //       : "assets/dashboard/menu/userViewer.png",
                //   text: "Viewers Only",
                //   textLevel: this.sideNavItem.text,
                //   submenu: [],
                //   key: this.sideNavItem.key,
                //   level: "meteringPoint",
                // };
                // this.sideNavItem.submenu?.push(viewerUsers);

                this.changeDetectorRef.detectChanges();
                this.changeDetectorRef.markForCheck();
                return this.expand();
              }
              this.loading = false;
            });
        }
      } else {
        return this.expand();
      }
    } else {
      return this.collapse();
    }
  }

  collapse() {
    // Hide any running loader when collapsing the menu
    this.loading = false;
    this.isActive = false;
    this.sideNavService.setExpanded(this.id, false);
    const navHeight = this.navElement.scrollHeight;
    const elementTransition = this.navElement.style.transition;
    this.navElement.style.transition = "";
    requestAnimationFrame(() => {
      this.navElement.style.height = navHeight + "px";
      this.navElement.style.transition = elementTransition;
      requestAnimationFrame(() => {
        this.navElement.style.height = 0 + "px";
        this.collapsed = true;
        this.changeDetectorRef.markForCheck();
      });
    });
  }

  expand() {
    this.changeDetectorRef.detectChanges();
    this.sideNavService.setExpanded(this.id, true);
    this.navElement.style.height = this.navElement.scrollHeight + "px";
    this.collapsed = false;
    this.isActive = true;
    this.sideNavService.expand$.next(this.hierarchyExtension);
    this.changeDetectorRef.detectChanges();
  }

  goToWalletDashboard( activeLevel: string, levelID: string, levelName: string, walletReady?: boolean ) {

    localStorage.setItem("activeLevel", activeLevel);
    localStorage.setItem("meteringPointID", levelID);
    localStorage.setItem("walletMeteringPointID", levelID);
    const readyFlag = typeof walletReady === "boolean" ? walletReady : (localStorage.getItem("walletReady") === "true");
    localStorage.setItem("walletReady", String(!!readyFlag));

    localStorage.removeItem("deviceID");
    localStorage.removeItem("device");

    this.meteringPoint = levelName;
    this.activeLevel = activeLevel;

    this.changeDetectorRef.detectChanges();

    //if(localStorage.getItem("active") == "dashboard"){
    if (localStorage.getItem("meteringPoint") != levelName) {
      this.router.navigateByUrl("/dashboard");
    }
    localStorage.setItem("meteringPoint", levelName);

    setTimeout(() => {
      this.router.navigateByUrl("/dashboard/wallet-dashboard");
    }, 500);
    //}
    this.subNavRight.ngOnInit();

    this.changeDetectorRef.detectChanges();
  }


  getAccountHierachy(levelID:any){
     let body = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
      },
    };

    let jsonF = "account_hierarchy.json";

    if (localStorage.getItem("pmLevel") == "2") {
      jsonF = "account_hierarchy_2.json";
    } else if (
      localStorage.getItem("pmLevel") == "3" ||
      localStorage.getItem("pmLevel") == "4"
    ) {
      jsonF = "account_hierarchy_3.json";
    }

    this.apiRequest
      .apiRequest("metering/account_hierarchy/", jsonF, body)
      .subscribe({
        next: (res: any) => {
          if (res.success) {
            if (res.data.meteringFarms) {
              let meteringFarmID: number = Number(levelID);
              let meteringFarmsData: any = res.data.meteringFarms;
             
              let getMeteringSectionName: any = this.getMeteringSectionName(meteringFarmsData, meteringFarmID);
              localStorage.setItem('farmMeteringSection', getMeteringSectionName);
            }
          }
          this.changeDetectorRef.detectChanges();
        },
        error: (err: any) => {
          console.error('Failed to fetch account hierarchy:', err);
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  //................navigation..............................

    getMeteringSectionsByMeteringFarm() {
    this.loading = true;
    // let body = {
    //   payload: {
    //     meteringFarmID: parseInt(
    //       localStorage.getItem("meteringFarmID") as string
    //     ),
    //     platform: "web",
    //   },
    // };


    let body: any = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
        parentType:'farm', //farm,section,point
        parentID: localStorage.getItem("meteringFarmID") as string,
        perPage:30,
        page:1
      },
    };

    const defaultOptions = {
      autohide: true,
      delay: 5000,
    };
    this.apiRequest
      .apiRequest(
        "metering/navigation/children/",
        "children.json",
        body
      )
      .pipe(
        finalize(() => {
          this.loading = false;
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe((res) => {
        if (res.data.meteringSections && res.data.meteringSections.length < 1) {
          this.modalService.open(this.noMeteringSections).result.then(
            (result) => {
              if (result === "addMeteringSection") {
                localStorage.setItem(
                  "meteringSectionAction",
                  "add_metering_section"
                );
                this.router.navigateByUrl(
                  "/dashboard/metering/meteringSection"
                );
                //window.location.assign("/dashboard/metering/meteringSection")
              }
                 if (result === "editMeteringFarm") {
                if (
                  this.meteringFarmManagement == "viewer" ||
                  this.meteringFarmManagement == "no-access"
                ) {
                  let activeLevel = ("Metering Farm " +
                    localStorage.getItem("meteringFarm")) as string;
                  this.toastService.show(
                    "Edit Warning",
                    "You do not have permissions to edit \n" + activeLevel,
                    {
                      headerClasses: "bg-warning text-light",
                      ...defaultOptions,
                    }
                  );
                } else {
                  this.router.navigateByUrl("/dashboard/metering");
                  localStorage.setItem(
                    "meteringFarmAction",
                    "update_metering_farm"
                  );
                  setTimeout(() => {
                    this.router.navigateByUrl(
                      "/dashboard/metering/meteringFarmForm"
                    );
                  }, 500);
                  //window.location.assign("/dashboard/metering/meteringSection")
                }
              }
            },
            (reason) => {}
          );
        }

        this.changeDetectorRef.detectChanges();
        this.loading = false;
      });
  }


  loadMoreSections(){
    if (this.loadingMoreSections) {
      return;
    }
    this.loadingMoreSections = true;

    // Determine parent farm to append into
    const parentFarm: any = (this.sideNavItem as any).parentRef;
    const parentFarmID: number = (this.sideNavItem.key as unknown as number) || Number(localStorage.getItem("meteringFarmID") || 0);

    // pagination state (persist per farm to avoid clashes)
    const pageKey = `navChildrenSectionPage:${parentFarmID}`;
    const savedPageRaw = localStorage.getItem(pageKey) || "1";
    const savedPage = parseInt(savedPageRaw);
    this.navigationSectionPage = (isNaN(savedPage) ? 1 : savedPage) + 1;

    const body: any = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
        parentType: 'farm',
        parentID: parentFarmID,
        perPage: this.navigationPerPage,
        page: this.navigationSectionPage,
      },
    };

    if(this.menuSearchTerm){
      body.payload.searchTerm = this.menuSearchTerm
    }

    this.apiRequest
      .apiRequest("metering/navigation/children/", "children.json", body)
      .subscribe(
        (data: any) => {
          if (data && data.success === true && data.data && Array.isArray(data.data.data)) {
            // Remove existing load more tile from the parent submenu before appending new items
            if (parentFarm && Array.isArray(parentFarm.submenu)) {
              parentFarm.submenu = parentFarm.submenu.filter((it: any) => it.type !== 'loadMoreSections');
            }

            // Append new sections into parent submenu
            data.data.data.forEach((element: any) => {
              const dataitem: SideNavItem = {
                icon: "",
                imgSrc: this.companyCheck == "landis"
                  ? "assets/landis-assets/images/meteringsectionicon.svg"
                  : "assets/dashboard/menu/building.svg",
                text: element.name,
                submenu: [],
                key: element.id,
                level: "meteringSection",
              } as any;
              parentFarm?.submenu?.push(dataitem);
            });

            // Re-append load more tile if there is a next page
            if (data.data.pagination && data.data.pagination.next != null) {
              const meteringSectionLoadingBtn: any = {
                type: 'loadMoreSections',
                text: 'Load More Sections',
                submenu: [],
                key: parentFarmID,
                level: 'meteringFarm',
                parentRef: parentFarm,
              };
              parentFarm?.submenu?.push(meteringSectionLoadingBtn as SideNavItem);
              localStorage.setItem(pageKey, String(this.navigationSectionPage));
            } else {
              // reset page tracking if no more pages
              localStorage.removeItem(pageKey);
            }
          }
          this.loadingMoreSections = false;
          this.changeDetectorRef.detectChanges();
        },
        (_err: any) => {
          this.loadingMoreSections = false;
          this.changeDetectorRef.detectChanges();
        }
      );
  }

  // Load more metering points for a metering section
  loadMorePoints(){
    if (this.loadingMorePoints) {
      return;
    }
    this.loadingMorePoints = true;

    const parentSection: any = (this.sideNavItem as any).parentRef;
    const sectionID: number = (this.sideNavItem.key as unknown as number);

    const pageKey = `navChildrenPointsPage:${sectionID}`;
    const savedPageRaw = localStorage.getItem(pageKey) || "1";
    const savedPage = parseInt(savedPageRaw);
    this.navigationPointsPage = (isNaN(savedPage) ? 1 : savedPage) + 1;

    const body: any = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
        parentType: 'section',
        parentID: sectionID,
        perPage: 5,
        page: this.navigationPointsPage,
      },
    };

    if(this.menuSearchTerm){
            body.payload.searchTerm = this.menuSearchTerm
    }

    this.apiRequest
      .apiRequest("metering/navigation/children/", "children.json", body)
      .subscribe(
        (data: any) => {
          if (data && data.success === true && data.data && Array.isArray(data.data.data)) {
            if (parentSection && Array.isArray(parentSection.submenu)) {
              parentSection.submenu = parentSection.submenu.filter((it: any) => it.type !== 'loadMorePoints');
            }

            data.data.data.forEach((element: any) => {
              const dataitem: SideNavItem = {
                icon: "",
                imgSrc: this.companyCheck == "landis"
                  ? "assets/landis-assets/images/verticalbuilding.svg"
                  : "assets/dashboard/menu/vertical-building.svg",
                text: element.name,
                totalMeters: element.totalMeters,
                submenu: [],
                key: element.id,
                level: "meteringPoint",
              } as any;
              parentSection?.submenu?.push(dataitem);
            });

            if (data.data.pagination && data.data.pagination.next != null) {
              const loadMorePointsBtn: any = {
                type: 'loadMorePoints',
                text: 'Load More Points',
                submenu: [],
                key: sectionID,
                level: 'meteringSection',
                parentRef: parentSection,
              };
              parentSection?.submenu?.push(loadMorePointsBtn as SideNavItem);
              localStorage.setItem(pageKey, String(this.navigationPointsPage));
            } else {
              localStorage.removeItem(pageKey);
            }
          }
          this.loadingMorePoints = false;
          this.changeDetectorRef.detectChanges();
        },
        (_err: any) => {
          this.loadingMorePoints = false;
          this.changeDetectorRef.detectChanges();
        }
      );
  }

  // Load more meters for a metering point
  loadMoreMeters(){
    if (this.loadingMoreMeters) {
      return;
    }
    this.loadingMoreMeters = true;

    const parentPoint: any = (this.sideNavItem as any).parentRef;
    const pointID: number = (this.sideNavItem.key as unknown as number);

    const pageKey = `navChildrenMetersPage:${pointID}`;
    const savedPageRaw = localStorage.getItem(pageKey) || "1";
    const savedPage = parseInt(savedPageRaw);
    this.navigationMetersPage = (isNaN(savedPage) ? 1 : savedPage) + 1;

    const body: any = {
      payload: {
        userID: parseInt(localStorage.getItem("userID") as string),
        parentType: 'point',
        parentID: pointID,
        isActive: 'y',
        perPage: 5,
        page: this.navigationMetersPage
      },
    };

    if(this.menuSearchTerm){
            body.payload.searchTerm = this.menuSearchTerm
    }

    this.apiRequest
      .apiRequest("metering/navigation/children/", "children.json", body)
      .subscribe((data: any) => {
        if (data.success == true && data.data.data && data.data.data.length > 0) {
          // Remove the current load more button
          if (parentPoint?.submenu) {
            const loadMoreIndex = parentPoint.submenu.findIndex((item: any) => item.type === 'loadMoreMeters');
            if (loadMoreIndex !== -1) {
              parentPoint.submenu.splice(loadMoreIndex, 1);
            }
          }

          // Add new meters to the existing submenu
          data.data.data.forEach((element: any) => {
            let dataitem: any = {
              icon: "",
              imgSrc: this.getMeterMenuIcon(element.meterType),
              text: element.name,
              submenu: null,
              key: element.deviceID,
              meterType: element.meterType,
              deviceType: element.deviceType,
              deviceVersion: element.deviceVersion ?? element.version ?? element.deviceModel ?? "",
              level: "device",
              isActive: element.isActive
            };
            parentPoint?.submenu?.push(dataitem);
          });

          // Add new load more button if there are more pages
          if (data.data.pagination && data.data.pagination.next != null) {
            const loadMoreMetersBtn: any = {
              type: 'loadMoreMeters',
              text: 'Load More Meters',
              submenu: [],
              key: pointID,
              level: 'meteringPoint',
              parentRef: parentPoint,
            };
            parentPoint?.submenu?.push(loadMoreMetersBtn as SideNavItem);
            localStorage.setItem(pageKey, String(this.navigationMetersPage));
          } else {
            localStorage.removeItem(pageKey);
          }
        }
        this.loadingMoreMeters = false;
        this.changeDetectorRef.detectChanges();
      },
      (_err: any) => {
        this.loadingMoreMeters = false;
        this.changeDetectorRef.detectChanges();
      }
    );
  }


}
